import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useAuth } from "./useAuth";

import { MS_IN_SECOND, QueryKey, SECONDS_IN_DAY } from "@/constants/shared";
import { makeRequest } from "@/network/api";
import { Quest } from "@/types";

export const useQuests = () => {
  const { address } = useAuth();

  const unescapeText = (text: string) => {
    // Unescape newline characters from backend
    return text.replace(/\\n/g, "\n");
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: [QueryKey.AllQuests, address],
    queryFn: async (): Promise<Quest[]> => {
      const response = await makeRequest(`quests?walletAddress=${address}`);
      const responseJson = await response.json();
      return responseJson.data.quests;
    },
    refetchOnMount: true, // refetch quests on page reload for updated quest completion %
  });

  const allQuests = useMemo(
    () =>
      data
        ?.map((quest) => ({
          ...quest,
          description: quest.description ? unescapeText(quest.description) : quest.description,
          isDoubleRewards: new Date(new Date(quest.liveAt).getTime() + 2 * SECONDS_IN_DAY * MS_IN_SECOND) > new Date(),
          doubleRewardsEndTime: new Date(new Date(quest.liveAt).getTime() + 2 * SECONDS_IN_DAY * MS_IN_SECOND),
          isUnrevealed: new Date(quest.liveAt) > new Date(),
        }))
        .filter((quest) => !quest.isUnrevealed) || ([] as Quest[]),
    [data],
  );

  const questsByAppId = useMemo(
    () =>
      allQuests.reduce(
        (map, quest) => {
          const relatedAppIdsForQuest = quest.tasks.map((task) => task.relatedAppId).filter(Boolean);
          relatedAppIdsForQuest.forEach((appId) => {
            map[appId] = [...(map[appId] || []), quest];
          });
          return map;
        },
        {} as { [appId: string]: Quest[] },
      ),
    [allQuests],
  );

  let headerQuest = allQuests?.find((quest) => quest.isDoubleRewards);
  if (!headerQuest) {
    headerQuest = allQuests?.find((quest) => quest.wave === 1);
  }

  return { allQuests, headerQuest, isLoadingQuests: isLoading, isErrorQuests: isError, questsByAppId };
};
